<script lang="ts">
  /**
   * One hexagon: how busy it is, what it feels like, and what has been reported there.
   *
   * The two numbers are shown as two separate things on purpose (brief §9a.3). A packed
   * platform can have a high GDI and a miserable vibe, and a layout that averaged them into
   * one bar would throw away the only comparison this screen is for.
   */
  import type { Attribute } from '$domain/attributes.ts';
  import { VIBE_MIN_VOTES } from '$domain/gdi.ts';
  import { request } from './api.ts';
  import Avatar from './Avatar.svelte';
  import { gdiColour, gdiLabel, vibeSymbol } from './gdi-scale.ts';
  import { describeError } from './session.svelte.ts';
  import { reverseGeocode } from './place.ts';
  import { FLAG_NOTE_MAX, FLAG_REASONS, reasonOf } from '$domain/flags.ts';
  import { REACTIONS } from '$domain/reactions.ts';
  import { flags } from './flags.svelte.ts';
  import { reactions } from './reactions.svelte.ts';
  import { shareCard } from './share.ts';
  import type { CellDetail } from './types.ts';

  type Props = {
    cell: string;
    attributes: Attribute[];
    onclose: () => void;
    onsighting: (id: string) => void;
  };

  const { cell, attributes, onclose, onsighting }: Props = $props();

  let detail = $state<CellDetail | undefined>();
  let error = $state('');
  let shared = $state('');

  // Asking for this cell's flag (amendment 13).
  let asking = $state(false);
  let reason = $state<string | undefined>();
  let note = $state('');
  let sending = $state(false);
  let askError = $state('');
  // Characters, as the server counts them — not UTF-16 units, which is what `maxlength` would
  // count, and which would stop an emoji-heavy note at half the limit the server allows.
  const noteLength = $derived([...note.trim()].length);

  async function sendRequest() {
    if (reason === undefined) return;
    sending = true;
    askError = '';
    try {
      await flags.ask(cell, reason, note);
      asking = false;
      note = '';
      detail = await request<CellDetail>(`/api/cells/${cell}`);
    } catch (cause) {
      askError = describeError(cause);
    } finally {
      sending = false;
    }
  }

  const byKey = $derived(new Map(attributes.map((entry) => [entry.key, entry])));

  const vibeText = $derived.by(() => {
    const score = detail?.score;
    if (score === undefined) return '';
    if (score.vibe === null) {
      return `noch zu wenig Stimmen (${score.vibeVotes} von ${VIBE_MIN_VOTES})`;
    }
    const words = { hot: 'läuft', meh: 'geht so', lame: 'lame' };
    const mood = score.vibe >= 40 ? 'hot' : score.vibe < -20 ? 'lame' : 'meh';
    return `${vibeSymbol(score.vibe)} ${words[mood]} (${score.vibe})`;
  });

  const facetLines = $derived.by(() => {
    const facets = detail?.score.facets ?? {};
    return Object.entries(facets).map(([key, value]) => {
      const attribute = byKey.get(key);
      const ends = attribute?.ends ?? ['', ''];
      // Below the midpoint the lower end describes it, above it the upper one. The number is
      // shown too, because "eher jung (1.4)" says more than either half alone.
      const end = value <= 2.5 ? ends[0] : ends[1];
      return `${attribute?.emoji ?? ''} eher ${end} (${value.toFixed(1)})`;
    });
  });

  $effect(() => {
    void (async () => {
      try {
        detail = await request<CellDetail>(`/api/cells/${cell}`);
        counts = { ...detail.reactions };
        error = '';
      } catch (cause) {
        error = describeError(cause);
      }
    })();
  });

  // Waben-Reaktionen (amendment 24): the last hour's, and the one just thrown.
  let counts = $state<Record<string, number>>({});
  let popped = $state<string | undefined>();
  let throwing = $state(false);
  let reactError = $state('');

  async function react(emoji: string) {
    throwing = true;
    reactError = '';
    try {
      await reactions.throwAt(cell, emoji);
      counts = { ...counts, [emoji]: (counts[emoji] ?? 0) + 1 };
      popped = emoji;
      setTimeout(() => (popped = undefined), 700);
    } catch (cause) {
      reactError = describeError(cause);
    } finally {
      // A moment between two throws: a tap is a throw, and a held thumb is not twenty.
      setTimeout(() => (throwing = false), 500);
    }
  }

  /**
   * Share the cell as a picture, never as a link (brief §17.2).
   *
   * There is no public URL to a cell and there is not going to be one — the instance is
   * invite-only and admin-approved, so a link would be either useless to the recipient or a
   * hole in the gate.
   */
  async function share() {
    const current = detail;
    if (current === undefined) return;
    const score = current.score;

    // The star means are computed here from the reports the sheet already has. They are not
    // in `cell_scores.facets` — that column holds the *scale* attributes, which is what the
    // map filters on (brief §7.1) — and adding them there would confuse two different things.
    const totals = new Map<string, { sum: number; count: number }>();
    for (const sighting of current.sightings) {
      for (const [key, value] of Object.entries(sighting.ratings)) {
        const entry = totals.get(key) ?? { sum: 0, count: 0 };
        totals.set(key, { sum: entry.sum + value, count: entry.count + 1 });
      }
    }
    const bars = attributes
      .filter((attribute) => attribute.kind === 'stars')
      .map((attribute) => {
        const entry = totals.get(attribute.key);
        return { attribute, value: entry === undefined ? 0 : entry.sum / entry.count };
      })
      .filter((bar) => bar.value > 0);

    const outcome = await shareCard({
      gdi: score.gdi,
      vibe: score.vibe,
      sightingCount: score.sightingCount,
      facets: score.facets,
      topAttribute: score.topAttribute,
      bars,
      // Looked up here rather than kept on the cell: the server never sees a coordinate and so
      // cannot name a place. Cached per cell in `place.ts`, and allowed to fail — a card with
      // no location is worse than one with, and far better than no card.
      place: await reverseGeocode(cell),
    });
    shared =
      outcome === 'copied'
        ? 'Bild in die Zwischenablage kopiert.'
        : outcome === 'unsupported'
          ? 'Teilen geht in diesem Browser nicht.'
          : '';
  }
</script>

<div class="backdrop" role="presentation" onclick={onclose}></div>

<section class="sheet" aria-label="Wabe">
  <header>
    <h2>Diese Wabe</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  {#if error}
    <p class="error" role="alert">{error}</p>
  {:else if detail === undefined}
    <p class="hint">Einen Moment …</p>
  {:else if !detail.public && detail.sightings.length === 0}
    <p class="hint">Hier ist noch nichts gemeldet.</p>
  {:else}
    <div class="numbers">
      <div class="gdi" style={`--gdi-colour: ${gdiColour(detail.score.gdi)}`}>
        <strong>{detail.score.gdi}</strong>
        <span>{gdiLabel(detail.score.gdi)}</span>
      </div>
      <div class="vibe">
        <span class="caption">Ort</span>
        <span>{vibeText}</span>
      </div>
    </div>

    {#if !detail.public}
      <p class="hint">
        Nur du siehst diese Wabe — ab zwei Meldungen erscheint sie auch für andere.
      </p>
    {:else}
      <!-- Waben-Reaktionen (amendment 24): thrown at the cell, rising on the maps of everybody
           looking, with nobody's name on them. -->
      <div class="react" role="group" aria-label="Auf die Wabe werfen">
        {#each REACTIONS as emoji (emoji)}
          <button
            class:popped={popped === emoji}
            disabled={throwing}
            onclick={() => react(emoji)}
            aria-label={`${emoji} auf die Wabe werfen`}
          >
            <span class="sign">{emoji}</span>
            {#if (counts[emoji] ?? 0) > 0}<small>{counts[emoji]}</small>{/if}
          </button>
        {/each}
      </div>
      <p class="hint">
        {reactError || 'Wirf etwas auf die Wabe: Wer gerade hinschaut, sieht es aufsteigen – ohne Namen.'}
      </p>
    {/if}

    {#if detail.flag}
      {@const holder = detail.flag.holder}
      <div class="flag">
        {#if detail.flag.mine}
          <p class="flag-line">🚩 <strong>Deine Flagge</strong> diese Woche</p>
        {:else}
          <p class="flag-line">
            🚩 Flagge von <Avatar username={holder.username} emoji={holder.avatarEmoji} />
            <strong>@{holder.username}</strong>
          </p>
          {#if detail.flag.myRequest}
            {@const asked = reasonOf(detail.flag.myRequest.reason)}
            <p class="hint">Angefragt: {asked.emoji} {asked.label} — jetzt entscheidet @{holder.username}.</p>
          {:else if asking}
            <form
              class="ask"
              onsubmit={(event) => {
                event.preventDefault();
                void sendRequest();
              }}
            >
              <p class="ask-title">Wabe von @{holder.username} anfragen</p>
              {#each FLAG_REASONS as option (option.key)}
                <label class="reason">
                  <input type="radio" name="flag-reason" value={option.key} bind:group={reason} />
                  <span>{option.emoji} {option.label}</span>
                </label>
              {/each}
              <textarea
                bind:value={note}
                rows="2"
                placeholder="Magst du noch was dazuschreiben? (freiwillig)"
                aria-label="Nachricht"
              ></textarea>
              <p class="count" class:over={noteLength > FLAG_NOTE_MAX}>
                {noteLength}/{FLAG_NOTE_MAX}
              </p>
              <div class="ask-verbs">
                <button type="button" class="quiet" onclick={() => (asking = false)}>Abbrechen</button>
                <button
                  type="submit"
                  disabled={reason === undefined || noteLength > FLAG_NOTE_MAX || sending}
                >
                  Anfrage senden
                </button>
              </div>
              {#if askError}<p class="error" role="alert">{askError}</p>{/if}
            </form>
          {:else}
            <button class="ask-open" onclick={() => (asking = true)}>Wabe anfragen</button>
          {/if}
        {/if}
      </div>
    {:else if detail.public}
      <p class="hint">🚩 Diese Woche noch keine Flagge — die nächste öffentliche Meldung setzt sie.</p>
    {/if}

    {#if detail.score.topAttribute}
      <p class="top">
        Am stärksten: {byKey.get(detail.score.topAttribute)?.emoji ?? ''}
        {byKey.get(detail.score.topAttribute)?.label ?? detail.score.topAttribute}
      </p>
    {/if}

    {#each facetLines as line (line)}
      <p class="facet">{line}</p>
    {/each}

    <button class="share" onclick={share}>Als Bild teilen</button>
    {#if shared}<p class="hint">{shared}</p>{/if}

    <ul class="sightings">
      {#each detail.sightings as sighting (sighting.id)}
        <li>
          <button onclick={() => onsighting(sighting.id)}>
            <span>@{sighting.username}</span>
            <span class="when">{new Date(sighting.observedAt).toLocaleString('de-DE')}</span>
            <span class="votes">👍 {sighting.confirmed} 👎 {sighting.disputed}</span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 40%);
    z-index: 5;
  }

  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 6;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-height: 80vh;
    overflow-y: auto;
    padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
    border-radius: 1.25rem 1.25rem 0 0;
    background: var(--card);
    box-shadow: 0 -8px 32px rgb(0 0 0 / 25%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .numbers {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .gdi {
    display: grid;
    place-items: center;
    min-width: 5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.75rem;
    background: color-mix(in srgb, var(--gdi-colour) 30%, transparent);
    border: 1px solid var(--gdi-colour);
  }

  .gdi strong {
    font-size: 1.75rem;
    line-height: 1;
  }

  .gdi span,
  .caption {
    font-size: 0.75rem;
    color: var(--muted);
  }

  .vibe {
    display: grid;
    gap: 0.15rem;
  }

  .top,
  .facet,
  .hint,
  .error {
    margin: 0;
    font-size: 0.875rem;
    color: var(--muted);
  }

  .error {
    color: var(--accent);
  }

  .share {
    align-self: flex-start;
    padding: 0.4rem 0.8rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .sightings {
    display: grid;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .sightings button {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
    padding: 0.5rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    text-align: left;
    cursor: pointer;
  }

  .when,
  .votes {
    color: var(--muted);
  }

  /* Waben-Reaktionen: six to throw, each with how often it was thrown in the last hour. */
  .react {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0.35rem;
    margin-top: 0.5rem;
  }

  .react button {
    position: relative;
    display: grid;
    place-items: center;
    padding: 0.35rem 0 0.3rem;
    border: 1px solid var(--border);
    border-radius: 0.7rem;
    background: var(--bg);
    cursor: pointer;
  }

  .react .sign {
    font-size: 1.35rem;
    line-height: 1.1;
  }

  .react small {
    color: var(--muted);
    font-size: 0.6875rem;
  }

  .react button.popped .sign {
    animation: thrown 0.7s ease-out;
  }

  @keyframes thrown {
    30% {
      transform: translateY(-0.6rem) scale(1.4);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .react button.popped .sign {
      animation: none;
    }
  }

  .flag {
    display: grid;
    gap: 0.4rem;
    padding: 0.6rem 0.75rem;
    border: 1px solid #ff4d4f;
    border-radius: 0.75rem;
    background: color-mix(in srgb, #ff4d4f 10%, var(--bg));
  }

  .flag-line {
    margin: 0;
    font-size: 0.9375rem;
  }

  .ask {
    display: grid;
    gap: 0.35rem;
  }

  .ask-title {
    margin: 0.2rem 0 0;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .reason {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
  }

  .ask textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 0.45rem 0.55rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font: inherit;
    font-size: 0.875rem;
    resize: vertical;
  }

  .count {
    margin: 0;
    font-size: 0.6875rem;
    color: var(--muted);
    text-align: right;
  }

  .count.over {
    color: var(--accent);
  }

  .ask-verbs {
    display: flex;
    justify-content: flex-end;
    gap: 0.4rem;
  }

  .ask-verbs button,
  .ask-open {
    padding: 0.4rem 0.8rem;
    border: 1px solid #ff4d4f;
    border-radius: 0.6rem;
    background: color-mix(in srgb, #ff4d4f 16%, var(--bg));
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .ask-verbs button.quiet {
    border-color: var(--border);
    background: var(--bg);
  }

  .ask-verbs button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .ask-open {
    justify-self: start;
  }
</style>
